import PageLayout from "components/LayoutContainers/PageLayout";
import { useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import { Grid, Icon, InputLabel } from "@mui/material";
import SoftTypography from "components/SoftTypography";
import Equipamiento from "./Equipamiento";
import SoftButton from "components/SoftButton";
import { getColor } from "utils/colors";
import { MessageManager } from "context";
import MateriaPrima from "./MateriaPrima";
import Efluentes from "./Efluentes";
import Infraestructura from "./Infraestructura";
import Vehiculos from "./Vehiculos";
import MedidasDeSeguridad from "./MedidasDeSeguridad";
import ResiduosGenerados from "./ResiduosGenerados";
import ResiduosReciclados from "./ResiduosReciclados";
import ResiduosTransportados from "./ResiduosTransportados";
import SoftBox from "components/SoftBox";
import Empresa from "./Empresa";
import FinalPdf from "./FinalPdf";

const GET_DECLARACION = gql`
  query getDeclaracion($id: ID!) {
    declaracionJurada(id: $id) {
      __typename @skip(if: true)
      id
      actividad
      estado
      calleLegal
      cpLegal
      provinciaLegal
      departamentoLegal
      localidadLegal
      telefonoLegal
      faxLegal
      calleReal
      cpReal
      provinciaReal
      departamentoReal
      localidadReal
      telefonoReal
      faxReal
      inicio
      fin
      anio
      cantidadManifiestos
      tasa
      montoTasa
      empresa {
        id
        razonSocial
        cuit
        email
        codigoAfip
        registroCatastral
        gerente
        habilitacionMunicipalNumero
        habilitacionMunicipalLugar
      }
      equipamientos {
        __typename @skip(if: true)
        id
        tipo
        potencia
        descripcion
        cantidad
      }
      materiasPrimas {
        __typename @skip(if: true)
        id
        compuestoQuimico
        unidad
        estado
        cantidadAnual
      }
      efluentesGaseosos {
        __typename @skip(if: true)
        id
        tipoDeResiduo
        descripcion
        dispositivoDeMedicion
        dispositivoDeDepuracion
        frecuenciaDeEmisiones
        tipoDeDispositivoDepuracion
        tipoDeDispositivoMedicion
        observaciones
        fileMedicion {
          id
          tipo
          file
        }
        fileDepuracion {
          id
          tipo
          file
        }
      }
      infraestructura {
        __typename @skip(if: true)
        id
        diasLaborales
        cantidadDeEmpleados
        aguaPotable
        aguaProcesoIndustrial
        potable
        especificacionPotable
        cruda
        especificacionCruda
        conexionCloacal
        cantidadConexionCloacal
        pozoAbsorvente
        cantidadPozoAbsorvente
        piletasLagunasDeTratamiento
        cantidadPiletasLagunasDeTratamiento
        plantaTratamiento
        cantidadPlantaTratamiento
        aguaNoIndustrial
        observacionAguaNoIndustrial
        aguaIndustrial
        observacionAguaIndustrial
        descripcionProcesoIndustrial
      }
      vehiculos {
        __typename @skip(if: true)
        id
        tipo
        marcaModelo
        dominio
        tallerPropio
      }
      medidasDeSeguridad {
        __typename @skip(if: true)
        id
        tipo
        descripcion
        observacion
      }
      residuosGenerados {
        __typename @skip(if: true)
        id
        tipoResiduo
        descripcionResiduo
        cantidadResiduo
        unidadResiduo
        observacion
        tipoTratamiento
        descripcionTratamiento
        cantidadTratamiento
        unidadTratamiento
      }
      residuosReciclados {
        __typename @skip(if: true)
        id
        tipoResiduo
        descripcionResiduo
        cantidadResiduo
        unidadResiduo
        tipoTratamiento
        descripcionTratamiento
        cantidadTratamiento
        unidadTratamiento
      }
      residuosTransportados {
        __typename @skip(if: true)
        id
        tipoResiduo
        descripcionResiduo
        cantidadResiduo
        estadoFisico
        descripcionTransporteAuditoria
      }
    }
  }
`;

function PdfDeclaracion() {
  const { id: idDeclaracion } = useParams();
  const { handleSnackbar } = useContext(MessageManager);
  const [empresa, setEmpresa] = useState(null);
  const [mostrarBoton, setMostrarBoton] = useState(true);

  const [listadoEquipamiento, setListadoEquipamiento] = useState([]);
  const [listadoMateriaPrima, setListadoMateriaPrima] = useState([]);
  const [listadoEfluentes, setListadoEfluentes] = useState([]);
  const [listadoVehiculos, setListadoVehiculos] = useState([]);
  const [listadoMedidas, setListadoMedidas] = useState([]);
  const [listadoResiduosGenerados, setListadoResiduosGenerados] = useState([]);
  const [listadoResiduosReciclados, setListadoResiduosReciclados] = useState([]);
  const [listadoResiduosTransportados, setListadoResiduosTransportados] = useState([]);
  const [infraestructura, setInfraestructura] = useState({});

  const [
    getDeclaracion,
    { loading: loadingDeclaracion, data: dataDeclaracion, error: errorDeclaracion },
  ] = useLazyQuery(GET_DECLARACION, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    getDeclaracion({
      variables: {
        id: idDeclaracion,
      },
    })
      .then((res) => {
        const { declaracionJurada } = res.data;
        setEmpresa(declaracionJurada.empresa);
        setListadoEquipamiento(declaracionJurada.equipamientos || []);
        setListadoMateriaPrima(declaracionJurada.materiasPrimas || []);
        setListadoEfluentes(declaracionJurada.efluentesGaseosos || []);
        setListadoVehiculos(declaracionJurada.vehiculos || []);
        setListadoMedidas(declaracionJurada.medidasDeSeguridad || []);
        setListadoResiduosGenerados(declaracionJurada.residuosGenerados || []);
        setListadoResiduosReciclados(declaracionJurada.residuosReciclados || []);
        setListadoResiduosTransportados(declaracionJurada.residuosTransportados || []);
        setInfraestructura(declaracionJurada.infraestructura || {});
      })
      .catch((e) => {
        handleSnackbar(e.message || "Error al obtener la declaración jurada", "error");
      });
  }, [getDeclaracion, idDeclaracion, handleSnackbar]);

  if (loadingDeclaracion) {
    return (
      <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <SoftTypography color="black" variant="h6">
          Cargando datos ...
        </SoftTypography>
        ;
      </SoftBox>
    );
  }

  if (errorDeclaracion) {
    return (
      <SoftTypography color="black" variant="h6">
        Error al cargar los datos de la empresa.
      </SoftTypography>
    );
  }
  const actividad = dataDeclaracion?.declaracionJurada?.actividad;
  const transportista = actividad == "Transportista";

  const handlePrint = () => {
    setMostrarBoton(false); // Ocultar el botón antes de imprimir
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        setMostrarBoton(true); // Mostrar el botón nuevamente después de imprimir
      }, 500);
    }, 300);
  };

  return (
    <PageLayout>
      <Grid
        sx={{
          m: 1,
        }}
      >
        <Grid
          display="flex"
          justifyContent="center"
          alignItems="center"
          px={3}
          flexDirection="column"
        >
          <SoftTypography color="black" variant="h4" fontWeight="bold">
            Declaración Jurada de Residuos Peligrosos
          </SoftTypography>
          <SoftTypography color="black" variant="h4" fontWeight="bold"></SoftTypography>
        </Grid>
        {/* <Grid p={3}>
          <SoftTypography color="black" variant="h4" fontWeight="bold" mb={2}>
            Datos de la Empresa
          </SoftTypography>
          {empresa ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <SoftTypography color="black" variant="subtitle2" fontWeight="bold">
                  Razón Social:
                </SoftTypography>
                <SoftTypography color="black" variant="body2">
                  {empresa.razonSocial || "Sin información"}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SoftTypography color="black" variant="subtitle2" fontWeight="bold">
                  CUIT:
                </SoftTypography>
                <SoftTypography color="black" variant="body2">{empresa.cuit || "Sin información"}</SoftTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SoftTypography color="black" variant="subtitle2" fontWeight="bold">
                  Email:
                </SoftTypography>
                <SoftTypography color="black" variant="body2">
                  {empresa.email || "Sin información"}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SoftTypography color="black" variant="subtitle2" fontWeight="bold">
                  Gerente:
                </SoftTypography>
                <SoftTypography color="black" variant="body2">
                  {empresa.gerente || "Sin información"}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SoftTypography color="black" variant="subtitle2" fontWeight="bold">
                  Código AFIP:
                </SoftTypography>
                <SoftTypography color="black" variant="body2">
                  {empresa.codigoAfip || "Sin información"}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SoftTypography color="black" variant="subtitle2" fontWeight="bold">
                  Registro Catastral:
                </SoftTypography>
                <SoftTypography color="black" variant="body2">
                  {empresa.registroCatastral || "Sin información"}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SoftTypography color="black" variant="subtitle2" fontWeight="bold">
                  Habilitación Municipal Número:
                </SoftTypography>
                <SoftTypography color="black" variant="body2">
                  {empresa.habilitacionMunicipalNumero || "Sin información"}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SoftTypography color="black" variant="subtitle2" fontWeight="bold">
                  Habilitación Municipal Lugar:
                </SoftTypography>
                <SoftTypography color="black" variant="body2">
                  {empresa.habilitacionMunicipalLugar || "Sin información"}
                </SoftTypography>
              </Grid>
            </Grid>
          ) : (
            <SoftTypography color="black" variant="h6">No se encontraron datos de la empresa.</SoftTypography>
          )}
        </Grid> */}
        <Empresa empresa={empresa} declaracion={dataDeclaracion?.declaracionJurada} />
      </Grid>
      {!transportista && listadoEquipamiento?.length > 0 && (
        <Grid sx={{ m: 1 }}>
          <Equipamiento
            idDeclaracion={idDeclaracion}
            listadoEquipamiento={listadoEquipamiento}
            setListadoEquipamiento={setListadoEquipamiento}
          />
        </Grid>
      )}
      {!transportista && listadoMateriaPrima?.length > 0 && (
        <Grid sx={{ m: 1 }}>
          <MateriaPrima
            idDeclaracion={idDeclaracion}
            listadoMateriaPrima={listadoMateriaPrima}
            setListadoMateriaPrima={setListadoMateriaPrima}
          />
        </Grid>
      )}
      <Grid sx={{ m: 1 }}>
        <Infraestructura
          infraestructura={infraestructura}
          setInfraestructura={setInfraestructura}
        />
      </Grid>
      {!transportista && listadoEfluentes?.length > 0 && (
        <Grid sx={{ m: 1 }}>
          <Efluentes
            idDeclaracion={idDeclaracion}
            listadoEfluentes={listadoEfluentes}
            setListadoEfluentes={setListadoEfluentes}
          />
        </Grid>
      )}
      {(actividad === "Generadora" || actividad == "Generador Comercio Servicio") &&
        listadoResiduosGenerados?.length > 0 && (
          <Grid sx={{ m: 1 }}>
            <ResiduosGenerados
              idDeclaracion={idDeclaracion}
              listadoResiduosGenerados={listadoResiduosGenerados}
              setListadoResiduosGenerados={setListadoResiduosGenerados}
            />
          </Grid>
        )}
      {actividad?.includes("Operador") && listadoResiduosReciclados?.length > 0 && (
        <Grid sx={{ m: 1 }}>
          <ResiduosReciclados
            idDeclaracion={idDeclaracion}
            listadoResiduosReciclados={listadoResiduosReciclados}
            setListadoResiduosReciclados={setListadoResiduosReciclados}
          />
        </Grid>
      )}
      {transportista && (
        <>
          {listadoResiduosTransportados?.length > 0 && (
            <Grid sx={{ m: 1 }}>
              <ResiduosTransportados
                idDeclaracion={idDeclaracion}
                listadoResiduosTransportados={listadoResiduosTransportados}
                setListadoResiduosTransportados={setListadoResiduosTransportados}
              />
            </Grid>
          )}
          {listadoVehiculos?.length > 0 && (
            <Grid sx={{ m: 1 }}>
              <Vehiculos
                idDeclaracion={idDeclaracion}
                listadoVehiculos={listadoVehiculos}
                setListadoVehiculos={setListadoVehiculos}
              />
            </Grid>
          )}
        </>
      )}
      {listadoMedidas?.length > 0 && (
        <Grid sx={{ m: 1 }}>
          <MedidasDeSeguridad
            idDeclaracion={idDeclaracion}
            listadoMedidas={listadoMedidas}
            setListadoMedidas={setListadoMedidas}
          />
        </Grid>
      )}
      <Grid sx={{ m: 1 }}>
        <FinalPdf declaracion={dataDeclaracion?.declaracionJurada} />
      </Grid>
      {mostrarBoton && (
        <Grid
          p={3}
          display={"flex"}
          justifyContent="flex-end"
          sx={{ position: "fixed", right: "0px", bottom: "30px" }}
        >
          <SoftButton
            variant="gradient"
            onClick={handlePrint}
            color="error"
            sx={{
              mr: 2,
              color: "#fff",
            }}
          >
            PDF
            <Icon sx={{ fontWeight: "bold", ml: 1 }}>picture_as_pdf</Icon>
          </SoftButton>
          <SoftButton
            variant="gradient"
            onClick={() => {
              window.location.href = `/declaraciones-juradas`;
            }}
            sx={{
              background: getColor("dark"),
              color: "#fff",
            }}
          >
            Salir
          </SoftButton>
        </Grid>
      )}
      <Grid mb={20}></Grid>
    </PageLayout>
  );
}

export default PdfDeclaracion;
